// RoomTab.tsx
import React, { useState } from "react";
import { Flex, Button, Group, Box, Divider, Transition, rem, Modal } from "@mantine/core";
import { FaFileExcel, FaHistory } from "react-icons/fa";
import { AirflowExcelImportSmart } from "../components/AirflowExcelImportSimple";
import { RoomsTable } from "./RoomsTable/RoomTable";
import { useAppSelector } from "../app/hooks";
import { RoomSideBar } from "../RoomSideBar/RoomSideBar";
import { useProjectId } from "../hooks/useProjectId"; // <-- adjust if needed
import { ProjectSyncRunsPanel } from "./ProjectSyncRunsPanel";
import { ProjectSyncStartButton } from "./ProjectSyncStartButton";
import PnsTreePanelContainer from "../DesignTab/routing/PnsTreePanelContainer";
import { selectIsSystemAdmin } from "../userSelectors";
import { useHydratePnsFromUrl } from "../hooks/useHydratePnsFromUrl";
import { usePnsQuerySync } from "../hooks/usePnsQuerySync";
import { useHydrateActiveRoomColoringIdFromUrl } from "../hooks/useHydrateActiveRoomColoringIdFromUrl";
import { useActiveRoomColoringIdQuerySync } from "../hooks/useActiveRoomColoringIdQuerySync";

const SIDEBAR_WIDTH = 600;
const PNS_PANEL_WIDTH = 360;

export const RoomTab: React.FC = () => {
  useHydratePnsFromUrl();
  usePnsQuerySync();
  useHydrateActiveRoomColoringIdFromUrl();
  useActiveRoomColoringIdQuerySync();

  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [syncRunsOpen, setSyncRunsOpen] = useState(false);

  const projectNoRaw = useProjectId(); // often string
  const projectNo = Number(projectNoRaw);
  const isSystemAdmin = useAppSelector(selectIsSystemAdmin);
  const copySpaceModeActive = useAppSelector((s) => s.design.copySpaceModeActive);

  const selectedSpaceNumber = useAppSelector((s) => s.design.selectedSpaceNumber);
  const isOpen = !!selectedSpaceNumber && !copySpaceModeActive;

  return (
    <Flex direction="column" h="100%" w="100%" p="md" gap="sm">
      {/* Knappraden */}
      <Group justify="flex-start" wrap="nowrap">
        {isSystemAdmin && <ProjectSyncStartButton projectNo={projectNo} />}
        <Button
          size="xs"
          variant="light"
          leftSection={<FaHistory size={14} />}
          onClick={() => setSyncRunsOpen(true)}
          radius="sm"
          disabled={!Number.isFinite(projectNo) || projectNo <= 0}
        >
          Synkroniseringshistorikk
        </Button>

        <Button
          size="xs"
          leftSection={<FaFileExcel size={14} />}
          onClick={() => setExcelImportOpen(true)}
          radius="sm"
        >
          Excelimport (tilluft/avtrekk)
        </Button>
      </Group>

      {/* Modaler */}
      <AirflowExcelImportSmart opened={excelImportOpen} onClose={() => setExcelImportOpen(false)} />

      <Modal
        opened={syncRunsOpen}
        onClose={() => setSyncRunsOpen(false)}
        title="Historikk for synkroniseringer"
        size="lg"
        centered
      >
        {/* Panelen har sin egen detalj-modal pr run */}
        <ProjectSyncRunsPanel projectNo={projectNo} />
      </Modal>

      <Divider my={rem(8)} />

      {/* Huvudinnehåll – flex som fyller resten */}
      <Flex flex={1} style={{ position: "relative", overflow: "hidden" }}>
        {/* Längst till vänster: PNS-panel */}
        <Box
          w={PNS_PANEL_WIDTH}
          h="100%"
          style={{
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <PnsTreePanelContainer />
        </Box>

        {/* Vänster: tabellen */}
        <Box style={{ flex: 1, minWidth: 0, overflow: "auto" }}>
          <RoomsTable />
        </Box>

        {/* Höger: sidebar */}
        <Transition mounted={isOpen} transition="slide-left" duration={400} timingFunction="ease">
          {(styles) => (
            <Box
              w={SIDEBAR_WIDTH}
              h="100%"
              style={{
                ...styles,
                borderLeft: "100%",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "white",
                overflowY: "auto",
                flexShrink: 0,
              }}
            >
              <RoomSideBar />
            </Box>
          )}
        </Transition>
      </Flex>
    </Flex>
  );
};
